import {
	ClientEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
	PatientEntityType,
	PractitionerEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateType } from "../document-template.types";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { countryLookup } from "../../lib/constants/countries";
import {
	allItemsHaveSameTp,
	getPatientLine,
	getPractitionerLine,
} from "./pdf-job-information-utils";
import { getClientFullName } from "@/lib/utils/client-utils";
import { invCoveredByOptions } from "@/dentlab/src/store/Jobs/utils";

const getInvCoveredByLabel = (value: string | null): string => {
	if (!value) return "";
	const option = invCoveredByOptions.find((opt) => opt.value === value);
	return option?.label ?? "";
};

const getInsuranceLabel = (
	invCoveredBy: string | null,
	insurance: string | null
): string => {
	const invCoveredByLabel = getInvCoveredByLabel(invCoveredBy);
	if (invCoveredByLabel && insurance) {
		return `(${invCoveredByLabel}, ${insurance})`;
	}
	if (invCoveredByLabel) {
		return `(${invCoveredByLabel})`;
	}
	if (insurance) {
		return `(${insurance})`;
	}
	return "";
};

export const PdfJobInformation: React.FC<{
	pdfTemplateType: PdfTemplateType;
	client: ClientEntityType;
	job: JobEntityType;
	jobItems: JobItemEntityType[];
	generalConfig: PdfTemplateConfiguration["general"];
	pdfTemplateConfiguration: PdfTemplateConfiguration;
	organization?: OrganizationEntityType;
	patient?: PatientEntityType;
	practitioner?: PractitionerEntityType;
	invoiceNumber?: string;
}> = ({
	pdfTemplateType,
	client,
	job,
	jobItems,
	organization,
	patient,
	practitioner,
	invoiceNumber,
	generalConfig,
	pdfTemplateConfiguration,
}) => {
	const lineHeight = "12px";

	const jobInformationRows: { label: string; value: string }[] = [];

	if (pdfTemplateType === PdfTemplateType.RECHNUNG) {
		jobInformationRows.push({
			label: "Rg.-Nummer:",
			value: invoiceNumber || "",
		});
	}

	let jobInfoLabel = "";
	let jobInfoValue = "";
	if (pdfTemplateType === PdfTemplateType.KOSTENVORANSCHLAG) {
		jobInfoLabel = "KV:";
		jobInfoValue = job.code as string;
	} else {
		jobInfoLabel = "Auftrag:";
		jobInfoValue = job.code as string;
	}
	jobInformationRows.push({ label: jobInfoLabel, value: job.code as string });

	if (job.title) {
		jobInformationRows.push({ label: "", value: job.title });
	}
	// If the job has a guarantor, we need to add the client the job belongs to
	if (job.guarantor_id) {
		jobInformationRows.push({
			label: "Auftraggeber:",
			value: getClientFullName(client),
		});
	}
	if (job.prod_country_choice || job.prod_country_other) {
		// If prod_country_choice is set, use it, otherwise use prod_country_other
		jobInformationRows.push({
			label: "Fertigung:",
			value: job.prod_country_choice
				? countryLookup[job.prod_country_choice] ??
					job.prod_country_choice
				: job.prod_country_other || "",
		});
	}
	if (job.patient_id && patient) {
		const patientLine = getPatientLine(patient, client);
		if (patientLine) {
			jobInformationRows.push({
				label: "Patient:",
				value: patientLine,
			});
		}
	}
	if (job.practitioner_id && practitioner) {
		jobInformationRows.push({
			label: "Behandler:",
			value: getPractitionerLine(practitioner),
		});
	}
	if (job.tooth_color) {
		jobInformationRows.push({
			label: "Zahnfarbe:",
			value: job.tooth_color,
		});
	}
	if (job.description) {
		jobInformationRows.push({
			label: "Beschreibung:",
			value: job.description,
		});
	}

	if (allItemsHaveSameTp(jobItems)) {
		let tpValueInformation =
			jobItems.find((i) => i.tp_value !== 0)?.tp_value?.toFixed(2) ?? "0";

		// Add insurance information for KV and LS templates
		if (
			(pdfTemplateType === PdfTemplateType.KOSTENVORANSCHLAG ||
				pdfTemplateType === PdfTemplateType.LIEFERSCHEIN) &&
			job
		) {
			let shouldShowInsurance = false;

			// Check client setting first (it overrides organization setting)
			if (
				client?.is_show_insurance_on_pdf !== undefined &&
				client?.is_show_insurance_on_pdf !== null
			) {
				shouldShowInsurance = client.is_show_insurance_on_pdf;
			} else {
				// Fall back to organization setting
				const orgConfig = organization?.config as any;
				shouldShowInsurance =
					orgConfig?.is_show_insurance_on_pdf ?? false;
			}

			if (shouldShowInsurance) {
				const insuranceLabel = getInsuranceLabel(
					job.inv_covered_by,
					job.insurance
				);
				if (insuranceLabel) {
					tpValueInformation += " " + insuranceLabel;
				}
			}
		}

		jobInformationRows.push({
			label: "Taxpt.-Wert:",
			value: tpValueInformation,
		});
	}

	const fontSize = {
		sm: "6pt",
		md: "7pt",
		lg: "9pt",
	};

	const fontSizeValue = generalConfig.size
		? fontSize[generalConfig.size]
		: undefined;

	return (
		<table>
			<tbody
				style={{
					fontSize: fontSizeValue,
				}}
			>
				{jobInformationRows.map((row, index) => {
					const isPatientRow = row.label === "Patient:";
					const isPractitionerRow = row.label === "Behandler:";
					const isPatientBold = generalConfig.isPatientBold ?? false;
					const isPractitionerBold =
						generalConfig.isPractitionerBold ?? false;

					const shouldBeBold =
						(isPatientRow && isPatientBold) ||
						(isPractitionerRow && isPractitionerBold);

					return (
						<tr key={index}>
							<td
								style={{
									paddingRight: "5mm",
								}}
							>
								<p
									style={{
										lineHeight,
										...(shouldBeBold
											? { fontWeight: "bold" }
											: {}),
									}}
								>
									{row.label}
								</p>
							</td>
							<td>
								<p
									style={{
										lineHeight,
										...(shouldBeBold
											? { fontWeight: "bold" }
											: {}),
									}}
								>
									{row.value}
								</p>
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
};
